import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Lock, Search, ShieldCheck, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Permission, useSession } from "@/components/session-provider";
import {
  allSections,
  searchSections,
  settingsGroups,
  type SettingsSection,
} from "@/lib/settings-catalog";
import { FeatureToggleList } from "@/components/settings/FeatureToggleList";
import { CannedResponsesPanel } from "@/components/settings/CannedResponsesPanel";
import { SlaSettingsPanel } from "@/components/settings/SlaPanel";
import { SignOutPanel } from "@/components/settings/SignOutPanel";
import { DirectorySyncPanel } from "@/components/settings/DirectorySyncPanel";
import { AiModelPanel } from "@/components/settings/AiModelPanel";
import { AiIdentityPanel } from "@/components/settings/AiIdentityPanel";
import { AiTeamsPanel } from "@/components/settings/AiTeamsPanel";
import { AssistantTranscriptsPanel } from "@/components/settings/AssistantTranscriptsPanel";
import { ExternalDomainsPanel } from "@/components/settings/ExternalDomainsPanel";
import { IntegrationsPanel } from "@/components/settings/IntegrationsPanel";
import { ConnectedAppsPanel } from "@/components/settings/ConnectedAppsPanel";
import { SecurityPosture } from "@/components/settings/SecurityPosture";
import { NotificationTemplatesPanel } from "@/components/settings/NotificationTemplatesPanel";
import { AttachmentFormatsPanel } from "@/components/settings/AttachmentFormatsPanel";
import { TicketLifecyclePanel } from "@/components/settings/TicketLifecyclePanel";
import { VipTitlesPanel } from "@/components/settings/VipTitlesPanel";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/settings")({
  /**
   * ⚠️ Shape only, and it is not a gate. `visibleSections` below still filters on the
   * server-issued permissions, and every panel's endpoints re-check regardless — so naming a
   * section in the URL reaches nothing that clicking it would not, and one the reader may not
   * open simply is not among the sections it can select from.
   */
  validateSearch: (search: Record<string, unknown>): { section?: string } => {
    // The section was "canned-responses" until D-153 renamed what people read; a link to the
    // old id lands on the renamed section rather than on the first one.
    const id = search.section === "canned-responses" ? "saved-replies" : search.section;

    return typeof id === "string" && allSections.some((s) => s.id === id) ? { section: id } : {};
  },
  component: SettingsPage,
});

export interface FeatureResponse {
  key: string;
  group: string;
  displayName: string;
  description: string;
  isEnabled: boolean;
  isLocked: boolean;
  lockedReason: string | null;
  updatedAtUtc: string;
}

/** Which API group each Features section renders. */
const FEATURE_GROUP_BY_SECTION: Record<string, string> = {
  "features-ticketing": "Ticketing",
  "features-assistance": "Assistance",
  "features-notifications": "Notifications",
  "features-access": "Access",
  "features-directory": "Directory",
};

function SettingsPage() {
  const { can } = useSession();
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  /**
   * Which section is open, from the URL.
   *
   * ⚠️ This was `useState`, so Settings was one address however deep into it somebody was:
   * "the notification templates are under here" could not be sent as a link, and a reload —
   * which happens on every quiet session refresh — dropped whoever was mid-task back to the
   * first section. The search box is deliberately NOT in the URL beside it: a half-typed
   * filter is not a place, and putting it there would make every keystroke a navigation.
   *
   * ⚠️ Rendering only. `visibleSections` filters on the server-issued permissions and each
   * panel's endpoints re-check, so a section named in a link that the reader may not open is
   * simply not among the ones `active` can resolve to.
   */
  const search: { section?: string } = Route.useSearch();
  const navigate = Route.useNavigate();

  const activeId = search.section ?? "features-ticketing";

  /** Replaces rather than pushes: moving around settings is not a history trail. */
  const setActiveId = (next: string) => void navigate({ search: { section: next }, replace: true });

  const [features, setFeatures] = useState<FeatureResponse[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Only sections the server-issued permissions cover. A section the user cannot see is
  // absent from the nav AND from search results — a search that surfaces a section you
  // then cannot open is worse than one that never mentioned it.
  const visibleSections = useMemo(
    () => allSections.filter((s) => !s.requires || can(s.requires)),
    [can],
  );

  const matches = useMemo(() => searchSections(query, visibleSections), [query, visibleSections]);

  const active: SettingsSection | undefined =
    matches.find((s) => s.id === activeId) ?? matches[0] ?? visibleSections[0];

  // "/" focuses search, Escape clears it — the convention people already have from every
  // other tool. Ignored while typing in a field so it does not hijack normal input.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;

      if (e.key === "/" && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }

      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery("");
        searchRef.current?.blur();
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const canReadSettings = can(Permission.SettingsRead);

  useEffect(() => {
    if (!canReadSettings) return;

    let cancelled = false;

    void (async () => {
      try {
        const result = await api<FeatureResponse[]>("/api/settings/features");
        if (!cancelled) setFeatures(result);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof ApiError ? e.message : "Could not load settings.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canReadSettings]);

  if (visibleSections.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-xl font-semibold">Settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You do not have access to any settings. Ask an administrator if you need it.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Turn features on and off, and connect external services.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        {/* -------------------------------------------------- Sidebar */}
        {/* `top-20`, not `top-6`: the shell header is sticky and 4rem tall, so anything stuck
            closer than that slides underneath it as the page scrolls. */}
        <nav
          aria-label="Settings sections"
          className="lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:self-start lg:overflow-y-auto"
        >
          <div className="relative mb-3">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search settings"
              aria-label="Search settings"
              className="w-full rounded-md border bg-background py-2 pl-8 pr-8 text-base outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>

          {matches.length === 0 ? (
            <p className="px-2 py-6 text-sm text-muted-foreground">
              Nothing matches &ldquo;{query}&rdquo;.
            </p>
          ) : (
            <ul className="space-y-4">
              {settingsGroups.map((group) => {
                const items = group.sections.filter((s) => matches.some((m) => m.id === s.id));
                if (items.length === 0) return null;

                return (
                  <li key={group.id}>
                    <h2 className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </h2>
                    <ul className="space-y-0.5">
                      {items.map((section) => {
                        const Icon = section.icon;
                        const isActive = active?.id === section.id;

                        return (
                          <li key={section.id}>
                            <button
                              type="button"
                              onClick={() => setActiveId(section.id)}
                              aria-current={isActive ? "page" : undefined}
                              className={cn(
                                "flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors",
                                isActive
                                  ? "bg-accent font-medium text-accent-foreground"
                                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                              )}
                            >
                              <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                              <span className="min-w-0">
                                <span className="block truncate">{section.label}</span>
                                <span className="block truncate text-xs font-normal text-muted-foreground">
                                  {section.description}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* -------------------------------------------------- Panel */}
        <main className="min-w-0">
          {loadError && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
            >
              {loadError}
            </div>
          )}

          {active && <Panel section={active} features={features} onFeaturesChange={setFeatures} />}
        </main>
      </div>
    </div>
  );
}

function Panel({
  section,
  features,
  onFeaturesChange,
}: {
  section: SettingsSection;
  features: FeatureResponse[] | null;
  onFeaturesChange: (next: FeatureResponse[]) => void;
}) {
  const Icon = section.icon;

  return (
    <section aria-labelledby={`${section.id}-heading`} className="group/card rounded-lg border">
      <header className="flex items-start gap-3 border-b px-4 py-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <h2 id={`${section.id}-heading`} className="font-medium">
              {section.label}
            </h2>
            <InfoTip id={`settings-${section.id}`} />
          </div>
          <p className="text-sm text-muted-foreground">{section.description}</p>
        </div>
      </header>

      <div className="p-4">
        {section.id === "sla" ? (
          <SlaSettingsPanel />
        ) : section.id === "saved-replies" ? (
          <CannedResponsesPanel />
        ) : section.id === "sign-out" ? (
          <SignOutPanel />
        ) : section.id === "external-domains" ? (
          <ExternalDomainsPanel />
        ) : section.id === "integrations" ? (
          <IntegrationsPanel />
        ) : section.id === "connected-apps" ? (
          <ConnectedAppsPanel />
        ) : section.id === "notification-templates" ? (
          <NotificationTemplatesPanel />
        ) : section.id === "attachment-formats" ? (
          <AttachmentFormatsPanel />
        ) : section.id === "vip-titles" ? (
          <VipTitlesPanel />
        ) : section.id === "security" ? (
          <SecurityPosture />
        ) : section.id === "ai-transcripts" ? (
          <AssistantTranscriptsPanel />
        ) : (
          <>
            <FeatureToggleList
              group={FEATURE_GROUP_BY_SECTION[section.id]}
              features={features}
              onFeaturesChange={onFeaturesChange}
            />

            {/* ⚠️ The interval, the last run and the Run now button live HERE, with the
                switch that turns the sync on — not in a separate section two clicks away.
                They were separate, and the split was the reason "did it actually run?"
                went unanswered: the toggle reported nothing and the report was somewhere
                nobody thought to look. One feature, one place. */}
            {section.id === "features-directory" && (
              <div className="mt-4 border-t pt-4">
                <DirectorySyncPanel />
              </div>
            )}

            {section.id === "features-assistance" && (
              <div className="mt-4 space-y-4 border-t pt-4">
                <AiIdentityPanel />
                <AiTeamsPanel />
                <AiModelPanel />
              </div>
            )}

            {section.id === "features-ticketing" && (
              <div className="mt-4 border-t pt-4">
                <TicketLifecyclePanel />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** Shared by the panels: the visual for a setting that cannot be switched on here. */
export function LockedNotice({ reason }: { reason: string }) {
  return (
    <p className="mt-2 flex items-start gap-1.5 rounded-md bg-muted px-2.5 py-2 text-xs text-muted-foreground">
      <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
      <span>{reason}</span>
    </p>
  );
}
